'use client';

import { postNotifyProduct, postNotifyPromoListed } from '@/api/product';
import { IGoodsDetail, IProductDetail, IVipPrice } from '@/api/types/public';

import Button, { CustomButtonProps } from '@/components/Button';
import SelectLensPromotionTag, {
  type LensPromotionLabel,
  resolveLensPromotionLabelTexts,
} from '@/components/GoodsDetail/SelectLensPromotionTag';
import Image from '@/components/Image';
import { ELoginRegisterCloseType } from '@/components/Layout/LoginRegister/types';
import Wish from '@/components/Product/Wish';
import { useStore } from '@/providers/app-store-provider';
import { getStoreState } from '@/store';
import { colors, font } from '@/theme/zeelool.theme';
import logger from '@/utils/client-logger';

import { env } from '@/utils/env';
import { IS_US } from '@/utils/constants';
import { Skeleton, SxProps } from '@mui/material';
import { useTranslations } from '@/i18n/use-translations';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import NotifyEmailDialog from './dialog/NotifyEmailDialog';
const ClientOnlyPrice = dynamic(() => import('@/components/Product/Price'), { ssr: false });

// 定义 StyledButton 的 props 接口
interface StyledButtonProps extends Omit<CustomButtonProps, 'variant' | 'sx'> {
  $variant?: 'outlined' | 'contained';

  $fullWidth?: boolean;
  $flex?: number | 'none';
  $width?: string;
  $minWidth?: string;
  $isPC?: boolean;
  sx?: any;
}

// 创建可复用的按钮组件
const StyledButton: React.FC<StyledButtonProps> = ({
  $variant = 'outlined',
  $fullWidth,
  $flex,
  $width,
  $isPC,
  $minWidth,
  sx,
  ...props
}) => {
  // 将自定义的 variant 映射到 MUI 支持的 variant
  const muiVariant = $variant;

  return (
    <Button
      variant={muiVariant}
      {...props}
      sx={{
        height: $isPC ? '47px' : '43px',
        borderRadius: $isPC ? '4px' : '22px',
        fontSize: $isPC ? '16px' : '14px',
        fontFamily: font.bb,
        width: $fullWidth ? '100%' : $width || 'auto',
        flex: $flex || 'auto',
        minWidth: $minWidth || 'auto',
        ...($variant === 'outlined' && {
          '&.MuiButton-loading': {
            color: colors.primaryPurpleDisable,
          },
        }),
        ...($variant === 'contained' &&
          props.color === 'black' && {
            '&.MuiButton-loading': {
              color: colors.primaryWhite,
            },
          }),
        // 合并传入的 sx 样式
        ...sx,
      }}
    />
  );
};

interface BottomButtonProps {
  detail: {
    product: IProductDetail;
    customer: IGoodsDetail['customer'];
    select_lens_config: IGoodsDetail['select_lens_config'];
    lens_promotion_label?: LensPromotionLabel | null;
    lensPromotionLabel?: LensPromotionLabel | null;
  };
  bottomButtonClick: (type: number, btnName: string) => void;
  addCartLoading: boolean;
  frameOnlyLoading: boolean;
  selectLensLoading: boolean;
  SDProductDetailsPageBtnClick?: (btnName: string) => void;
  className?: string;
  isWeb?: boolean;
  isFixed?: boolean;
  glassesFilter?: string;
  clientRenderReady?: boolean;
  onWishChange?: (isWish: number, delta: number) => void; // 收藏状态变化回调，delta 为 wish_num 的变化量（+1 或 -1）
}

// 按钮配置类型
interface ButtonConfig {
  type: number;
  name: string;
  variant: 'outlined' | 'contained';
  loading: boolean;
  width?: string;
  flex?: number | 'none';
  hasDiscount?: boolean;
  minWidth?: string;
  sx?: SxProps;
}

const selectLensPromotionTagClassName =
  'z-[3] absolute -top-[12px] right-[9px] block h-[24px] rounded-[12px] max-w-[100%] box-border overflow-hidden whitespace-nowrap bg-auxiliary-rose-red px-[6px] py-[5px] text-left font-bs text-[12px] leading-[14px] text-primary-text';

const wapSelectLensPromotionTagClassName =
  'z-[3] absolute -top-[9px] right-[9px] block h-[18px] rounded-[9px] max-w-[100%] overflow-hidden whitespace-nowrap bg-auxiliary-rose-red px-[7px] text-center font-br text-11 leading-[18px] text-primary-text';

const BottomButton: React.FC<BottomButtonProps> = ({
  detail,
  bottomButtonClick,
  addCartLoading,
  frameOnlyLoading,
  selectLensLoading,
  SDProductDetailsPageBtnClick,
  className,
  isWeb = false,
  isFixed = true,
  glassesFilter,
  clientRenderReady = false,
  onWishChange,
}) => {
  const t = useTranslations('goodsDetail.bottomButton');
  const tPriceA11y = useTranslations('goodsDetail.clientPrice');
  const product = detail?.product;
  const customer = detail?.customer;
  const token = useStore((state) => state.token);
  const userInfo = useStore((state) => state.userInfo);
  const setShowLoginRegisterDialog = useStore((state) => state.setShowLoginRegisterDialog);
  const setOnLoginClose = useStore((state) => state.setOnLoginClose);
  const [showNotifyDialog, setShowNotifyDialog] = useState<boolean>(false);
  const [notifyEmailLoading, setNotifyEmailLoading] = useState<boolean>(false);
  const [promoListedNotifyLoading, setPromoListedNotifyLoading] = useState<boolean>(false);
  // 乐观更新：订阅成功后本地标记为已订阅
  const [promoListedSubscribedLocally, setPromoListedSubscribedLocally] = useState<boolean>(false);
  // 登录前点击了订阅按钮，等待登录后 detail 刷新再决定是否调接口
  const [clickedPromoNotify, setClickedPromoNotify] = useState<boolean>(false);
  // 用 ref 追踪 product 引用，检测 detail 是否已完成刷新
  const productRef = useRef(product);

  // 使用useMemo优化计算显示状态
  const displayStates = useMemo(() => {
    const promoListedStatusValue = product?.promo_listed_status;
    return {
      showStockBtn: product?.status === 2,
      showSoldOutBtn: (product?.qty || 0) < 1,
      isGlasses: product?.is_glass === 1,
      isProductLens: product?.product_lens === 1,
      showFrameOnly: product?.is_frame_only === 1,
      showSelectLens: product?.is_show_add_lense === 1,
      showAddToCart: product?.is_show_add_to_cart === 1,
      // promo_listed_status: 1=未订阅, 2=已订阅, 3=普通商品
      isPromoListedSubscribed: promoListedStatusValue === 2,
      isPromoListed: promoListedStatusValue === 1 || promoListedStatusValue === 2,
    };
  }, [
    product?.status,
    product?.qty,
    product?.is_glass,
    product?.product_lens,
    product?.is_frame_only,
    product?.is_show_add_lense,
    product?.is_show_add_to_cart,
    product?.promo_listed_status,
  ]);

  const {
    showStockBtn,
    showSoldOutBtn,
    isGlasses,
    isProductLens,
    showFrameOnly,
    showSelectLens,
    showAddToCart,
    isPromoListedSubscribed: isPromoListedSubscribedFromServer,
    isPromoListed,
  } = displayStates;
  const lensPromotionLabel = detail?.lens_promotion_label ?? detail?.lensPromotionLabel;
  const hasSelectLensPromotion = useMemo(
    () => resolveLensPromotionLabelTexts(lensPromotionLabel).length > 0,
    [lensPromotionLabel],
  );

  const isPromoListedSubscribed = isPromoListedSubscribedFromServer || promoListedSubscribedLocally;

  // 处理按钮点击
  const handleButtonClick = (type: number, btnName: string) => {
    if (addCartLoading || selectLensLoading || frameOnlyLoading) {
      return;
    }
    bottomButtonClick(type, btnName);
    SDProductDetailsPageBtnClick?.(btnName);
  };

  // 邮箱通知（售罄）
  const notifyEmail = useCallback(async () => {
    const productId = product?.id || 0;

    if (token) {
      try {
        setNotifyEmailLoading(true);
        await postNotifyProduct({
          email: userInfo.email,
          id: productId,
        });
      } catch (error) {
        logger.error('notifyEmail error', error);
      } finally {
        setNotifyEmailLoading(false);
      }
    } else if (IS_US) {
      /**
       * 仅对北美站特殊处理
       * 用户点击到货通知，打开常规登录注册弹窗
       * 用户登录/注册成功，直接提示订阅成功
       */
      setOnLoginClose(async (type?: ELoginRegisterCloseType) => {
        const authSuccessTypes = [ELoginRegisterCloseType.LOGIN, ELoginRegisterCloseType.REGISTER];
        if (!type || !authSuccessTypes.includes(type)) {
          return;
        }

        const latestUserInfo = getStoreState().userInfo;
        if (!latestUserInfo?.email) {
          return;
        }

        try {
          setNotifyEmailLoading(true);
          await postNotifyProduct({
            email: latestUserInfo.email,
            id: productId,
          });
        } catch (error) {
          logger.error('notifyEmail after login error', error);
        } finally {
          setNotifyEmailLoading(false);
        }
      });
      setShowLoginRegisterDialog({
        show: true,
        eventType: 'other',
        situation: '商详页到货通知',
      });
    } else {
      setShowNotifyDialog(true);
    }
  }, [product?.id, setOnLoginClose, setShowLoginRegisterDialog, token, userInfo.email]);

  // 即将上市订阅通知
  const notifyPromoListed = useCallback(async () => {
    if (token) {
      try {
        setPromoListedNotifyLoading(true);
        await postNotifyPromoListed({
          email: userInfo.email,
          id: product?.id || 0,
        });
        setPromoListedSubscribedLocally(true);
      } catch (error) {
        logger.error('notifyPromoListed error', error);
      } finally {
        setPromoListedNotifyLoading(false);
      }
    } else {
      setClickedPromoNotify(true);
      setShowLoginRegisterDialog({ show: true, situation: '商详即将上市订阅' });
    }
  }, [token, userInfo, product, setShowLoginRegisterDialog]);

  // 登录后 detail 刷新完成时，根据服务端最新 promo_listed_status 决定是否调订阅接口
  useEffect(() => {
    // product 引用未变化（尚未刷新），跳过
    if (product === productRef.current) return;
    productRef.current = product;

    if (!clickedPromoNotify || !token) return;

    setClickedPromoNotify(false);
    // detail 刷新后服务端已是已订阅状态，无需再调接口
    if (product?.promo_listed_status === 2) return;
    notifyPromoListed();
  }, [product, clickedPromoNotify, token, notifyPromoListed]);

  // 计算按钮配置
  const getButtonConfigs = (): ButtonConfig[] => {
    const configs: ButtonConfig[] = [];

    // 非眼镜或不支持配镜的商品
    if (!isGlasses || !isProductLens) {
      configs.push({
        type: 0,
        width: '100%',
        name: t('addToCart'),
        variant: 'contained',
        loading: addCartLoading,
      });
      return configs;
    }

    // 眼镜且支持配镜的商品
    if (isGlasses && isProductLens) {
      // Select Lenses按钮
      if (showSelectLens) {
        const width = isWeb && showFrameOnly ? '284px' : '100%';
        const minWidth = isWeb ? '284px' : 'auto';

        configs.push({
          type: 1,
          name: t('selectLenses'),
          variant: 'contained',
          loading: selectLensLoading,
          flex: 1,
          hasDiscount: hasSelectLensPromotion,
          width,
          minWidth,
        });
      }

      // Frame Only按钮
      if (showFrameOnly) {
        let width = '32vw'; // 默认移动端宽度
        let variant: 'outlined' | 'contained' = 'outlined';
        let flex: number | 'none' = 'none';
        let minWidth = 'auto';

        // 计算宽度
        if (isWeb) {
          width = '204px';
          minWidth = '166px';
        } else if (!showSelectLens && !showAddToCart) {
          width = '100%';
          flex = 1;
        }

        if (showSelectLens && isWeb) {
          width = '172px';
          flex = 1;
        }

        // 计算variant
        if (!showSelectLens && showFrameOnly && !showAddToCart) {
          variant = 'contained';
        }

        configs.push({
          type: 2,
          name: t('frameOnly'),
          variant,
          loading: frameOnlyLoading,
          width,
          flex,
          minWidth,
          sx: {
            '&.MuiButton-loading': {
              color: 'var(--zeelool-palette-action-disabled)',
            },
          },
        });
      }

      // Add To Cart按钮
      if (showAddToCart) {
        let width = '45.33vw'; // 默认移动端宽度
        let variant: 'contained' | 'outlined' = 'outlined';
        let flex: number | 'none' = 'none';
        const minWidth = 'auto';
        // 计算宽度
        if (!showSelectLens && !showFrameOnly) {
          width = '100%';
          flex = 1;
        }
        // 计算variant
        if (!showSelectLens && !showFrameOnly && showAddToCart) {
          variant = 'contained';
        }

        configs.push({
          type: 0,
          name: t('addToCart'),
          variant,
          loading: addCartLoading,
          width,
          flex,
          minWidth,
        });
      }
    }

    return configs;
  };

  const buttonConfigs = getButtonConfigs();

  // 渲染单个按钮
  const renderButton = (config: ButtonConfig) => {
    let buttonWidth = 'auto';
    if (config.width) {
      buttonWidth = config.width;
    } else if (config.flex === 1) {
      buttonWidth = '100%';
    }

    const linkHref = {
      0: '/cart',
      1: `/lens-process?sku=${product?.shop_sku}`,
      2: `/frame-process?sku=${product?.shop_sku}`,
    }[config.type];

    const buttonElement = (
      <StyledButton
        key={config.name}
        $variant={config.variant}
        $width={buttonWidth}
        $flex={config.flex}
        $isPC={isWeb}
        $minWidth={config.minWidth}
        onClick={() => handleButtonClick(config.type, config.name)}
        loading={config.loading}
        disabled={config.loading}
        className={config.hasDiscount ? 'z-[2] relative' : undefined}
        sx={config.sx || {}}
        style={env.NEXT_PUBLIC_SITE_NAME === 'fr' ? { lineHeight: 1.2 } : {}}
      >
        <Link
          href={linkHref}
          onClick={(e) => {
            e.preventDefault();
          }}
        >
          {config.name}
        </Link>
      </StyledButton>
    );

    // 如果有折扣标签，包装在div中
    if (config.hasDiscount) {
      const discountButtonWrapperClassName =
        isWeb && buttonWidth !== '100%' ? 'relative flex-none' : 'relative flex-1';

      return (
        <div key={config.name} className={discountButtonWrapperClassName}>
          <SelectLensPromotionTag
            label={lensPromotionLabel}
            className={isWeb ? selectLensPromotionTagClassName : wapSelectLensPromotionTagClassName}
          />
          {buttonElement}
        </div>
      );
    }

    return buttonElement;
  };

  // 渲染按钮组
  const renderButtons = () => {
    // 即将上市订阅状态优先级最高（覆盖下架/售罄逻辑）
    if (isPromoListed) {
      return (
        <StyledButton
          $variant="contained"
          color="black"
          $fullWidth
          $isPC={isWeb}
          onClick={() => {
            if (isPromoListedSubscribed) return;
            notifyPromoListed();
            SDProductDetailsPageBtnClick?.('Notify me when available');
          }}
          loading={promoListedNotifyLoading}
          disabled={promoListedNotifyLoading || isPromoListedSubscribed}
        >
          {isPromoListedSubscribed ? t('subscribed') : t('notifyMeWhenAvailable')}
        </StyledButton>
      );
    }

    // 下架状态
    if (showStockBtn) {
      return (
        <StyledButton $variant="contained" $fullWidth $isPC={isWeb} disabled>
          {t('noRestock')}
        </StyledButton>
      );
    }

    // 售罄状态
    if (showSoldOutBtn) {
      return (
        <StyledButton
          $variant="contained"
          color="black"
          $fullWidth
          $isPC={isWeb}
          onClick={() => {
            notifyEmail();
            SDProductDetailsPageBtnClick?.('Notify me when in stock');
          }}
          loading={notifyEmailLoading}
          disabled={notifyEmailLoading}
        >
          {t('notifyMeWhenInStock')}
        </StyledButton>
      );
    }

    // 正常商品状态
    return buttonConfigs.map(renderButton);
  };

  // 渲染底部提示条（售罄 / 即将上市）
  const renderOutOfStockBanner = () => {
    let bannerText: string | null = null;
    // isPromoListed 优先级最高
    if (isPromoListed) {
      bannerText = isPromoListedSubscribed
        ? t('promoListedSubscribed')
        : t('promoListedUnsubscribed');
    } else if (showSoldOutBtn && !showStockBtn) {
      bannerText = t('outOfStock');
    }

    if (!bannerText) return null;

    let bannerClass =
      'fixed bottom-[71px] left-0 w-full h-[32px] bg-[#e9ffbc] text-primaryText text-12 flex items-center pl-14 z-[100]';

    if (isWeb) {
      bannerClass =
        'w-full h-[32px] bg-[#e9ffbc] text-primaryText text-12 leading-[32px] pl-4 mb-4 rounded text-center';
    }

    return <div className={bannerClass}>{bannerText}</div>;
  };

  // 渲染PC端固定底部的商品信息
  const renderPCProductInfo = () => {
    if (!isWeb || !isFixed) return null;
    return (
      <div className="flex items-center w-[calc(88px+38.3vw)]">
        {/* 商品图片 */}
        <div className="mr-16 flex flex-shrink-0 items-center justify-center overflow-hidden rounded-6 bg-[#eeeeee] h-[54px] w-[72px]">
          <Image
            src={(product?.image?.product?.[0] as any)?.url || ''}
            alt={product?.short_name || ''}
            className="h-auto object-contain mix-blend-multiply w-[54px]"
            width={60}
            height={60}
          />
        </div>

        {/* 商品信息 */}
        <div className="flex min-w-0">
          <div>
            <h3 className="font-bb text-20 mb-[5px] leading-24">
              {product?.short_name}
              {glassesFilter}
            </h3>
            <p className="text-[#a9a9a9] text-[14px]">{product?.name || ''}</p>{' '}
          </div>
        </div>

        {/* 价格信息 */}
        <ClientOnlyPrice
          priceObject={product as unknown as IVipPrice}
          renderPrice={(price, sellPrice) => {
            return (
              <div className="ml-auto flex items-center whitespace-nowrap">
                <span className="font-bb text-24" aria-label={tPriceA11y('srCurrent') + sellPrice}>
                  {sellPrice}
                </span>
                {price !== sellPrice && (
                  <span className="ml-8 text-16 text-[#a9a9a9] line-through">
                    <del aria-label={tPriceA11y('srOriginal') + price}>{price}</del>
                  </span>
                )}
              </div>
            );
          }}
        />
      </div>
    );
  };

  // 渲染收藏按钮
  const renderWishButton = () => {
    if (isWeb) return null; // PC端不显示收藏按钮（在产品信息中处理）

    return (
      <div className="mr-0 flex flex-col items-center justify-center text-center w-[56px]">
        {clientRenderReady ? (
          <Wish
            data={{
              id: product?.id || 0,
              shop_sku: product?.shop_sku || '',
              name: product?.short_name || '',
              short_name: product?.short_name || '',
              wish_num: product?.wish_num || 0,
              is_wish: customer?.is_wish || 2,
              business_sku: product?.business_sku || product?.shop_sku || '',
              sku: product?.sku || product?.shop_sku || '',
            }}
            className="flex flex-col items-center justify-center"
            iconClassName=""
            wishNumClassName="text-12 text-center block notranslate mt-1"
            iconSize="26px"
            showWishNum
            loginSituation="商详页收藏"
            collectScene="商详页收藏"
            wished={() => {
              // 当收藏成功时，通知父组件更新状态（is_wish=1, wish_num+1）
              onWishChange?.(1, 1);
            }}
            removed={() => {
              // 当取消收藏成功时，通知父组件更新状态（is_wish=2, wish_num-1）
              onWishChange?.(2, -1);
            }}
          />
        ) : (
          <Skeleton variant="rectangular" width={26} height={26} />
        )}
      </div>
    );
  };

  // 渲染主要内容
  const renderMainContent = () => {
    // PC端非固定样式
    if (isWeb && !isFixed) {
      return (
        <>
          <section className={`w-full ${className}`}>
            <div className="flex items-center">
              <div className="flex w-full flex-1 gap-[12px]">{renderButtons()}</div>
            </div>
          </section>
          {renderOutOfStockBanner()}
        </>
      );
    }
    if (isWeb && isFixed && (showStockBtn || showSoldOutBtn || isPromoListed)) {
      return null;
    }
    // PC端固定样式
    if (isWeb && isFixed) {
      return (
        <>
          {renderOutOfStockBanner()}
          <section
            className={`fixed bottom-0 left-0 right-0 z-[100] flex w-full items-center bg-white h-[80px] ${className}`}
            style={{ boxShadow: '0px -2px 10px rgba(0,0,0,0.1)' }}
          >
            <div className="mx-auto flex w-full items-center justify-center gap-[2.5vw]">
              {renderPCProductInfo()}
              <div className="flex flex-nowrap items-center w-[472px]">
                <div className="flex flex-1 gap-[12px]">{renderButtons()}</div>
              </div>
            </div>
          </section>
        </>
      );
    }

    // 移动端固定样式
    return (
      <>
        {renderOutOfStockBanner()}
        <section
          className={`fixed bottom-0 left-0 right-0 z-[100] flex items-center bg-white h-[71px] pl-[3px] pr-[14px] py-[14px] ${className}`}
          style={{ boxShadow: '0px -1px 5px 0px rgba(204,204,204,0.5)' }}
        >
          {renderWishButton()}
          <div className="flex flex-1 flex-row-reverse justify-between gap-12">
            {renderButtons()}
          </div>
        </section>
      </>
    );
  };

  return (
    <>
      {renderMainContent()}
      {/* 售罄通知对话框 */}
      <NotifyEmailDialog
        open={showNotifyDialog}
        productId={product?.id || 0}
        onClose={() => setShowNotifyDialog(false)}
        SDProductDetailsPageBtnClick={SDProductDetailsPageBtnClick}
        isWeb={isWeb}
      />
    </>
  );
};

export default BottomButton;
